import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  makeStyles,
  TextField,
  Table, TableCell, TableRow, TableHead, TableBody
} from "@material-ui/core";
import { LcmDialogProps } from "./DialogProps";
import { Controller, useForm } from "react-hook-form";
import { nameof } from "ts-simple-nameof";
import { fieldLabels } from "../../sections/fieldlabels";
import { Error } from "../../common/Error";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { usersService } from "../../../services";
import { Select } from "@ofqual/portal-components/components/Select";
import { addTimeRecord } from "../../../redux/actions";
import { useDispatch } from "react-redux";
import { LcmTimeRecord } from "../../../domain/LcmTimeRecord";
import { UserRole, UserType } from "../../../redux/types";

const defaultValues: Partial<LcmTimeRecord> = {
  name: null,
  externalCounsel: null,
  notes: null,
  timeDays: null,
  timeHours: null,
  timeMinutes: null,
  forecast: null,
};

const useStyles = makeStyles(() => ({
  actions: {
    padding: 15,
  },
}));

export const AddTimeRecordDialog: React.FC<LcmDialogProps> = ({
  lcmItem,
  onClose,
}) => {
  const { handleSubmit, control, errors } = useForm<LcmTimeRecord>({
    defaultValues,
  });

  const classes = useStyles();

  const userType = UserType.Ofqual;
  const [legalLookups] = useLookup({
    service: usersService,
    id: "upn",
    label: (item) => (item.fullName ? item.fullName : item.upn),
    params: {
      userType,
      role: [
        UserRole.Director,
        UserRole.CaseManager,
        UserRole.GC,
        UserRole.LPM,
      ],
      active: true,
    },
  },);

  const dispatch = useDispatch();

  const onSubmit = (req: LcmTimeRecord) => {
    if ((req.timeDays ?? 0) + (req.timeHours ?? 0) + (req.timeMinutes ?? 0) === 0) {

      errors.timeDays = { message: "Value must be greater than zero", type: "min" }
    }
    else {
      dispatch(addTimeRecord(lcmItem.id, req));
      onClose();
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="add-time-dialog"
    >
      <DialogTitle data-test="activity-title">Record Time / Cost</DialogTitle>
      <Divider component="div" />
      <DialogContent style={{ minHeight: "250px" }}>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={nameof<LcmTimeRecord>((x) => x.name)}
                as={
                  <Select
                    label={fieldLabels.name}
                    data-test="add-time-name-field"
                  >
                    {legalLookups}
                  </Select>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={nameof<LcmTimeRecord>((x) => x.externalCounsel)}
                as={
                  <TextField
                    variant="outlined"
                    label={fieldLabels.externalCounsel}
                    data-test="add-time-external-counsel-field"
                    fullWidth
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={nameof<LcmTimeRecord>((x) => x.notes)}
                render={({ onChange, value }) => (
                  <>
                    <label>{fieldLabels.note}</label>
                    <MarkdownEditor
                      onChange={onChange}
                      value={value}
                      dataTest="add-time-note-text-field"
                    />
                  </>
                )}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={3}>
                    <b>Time record*</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <Controller
                    control={control}
                    name={nameof<LcmTimeRecord>((x) => x.timeDays)}
                    rules={{ min: 0 }}
                    as={
                      <TableCell>
                        <TextField
                          type="number"
                          variant="outlined"
                          data-test="add-time-days-field"
                          fullWidth
                          placeholder={"dd"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                &nbsp;days
                              </InputAdornment>
                            ),
                          }}
                          defaultValue={0}
                        />
                      </TableCell>
                    }
                  />
                  <Controller
                    control={control}
                    name={nameof<LcmTimeRecord>((x) => x.timeHours)}
                    rules={{

                      max: 23,
                      min: 0,
                    }}
                    as={
                      <TableCell>
                        <TextField
                          type="number"
                          variant="outlined"
                          data-test="add-time-hours-field"
                          fullWidth
                          placeholder={"hh"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                &nbsp;hours
                              </InputAdornment>
                            ),
                          }}
                          defaultValue={0}
                        />
                      </TableCell>
                    }
                  />
                  <Controller
                    control={control}
                    name={nameof<LcmTimeRecord>((x) => x.timeMinutes)}
                    rules={{

                      max: 59,
                      min: 0
                    }}
                    as={
                      <TableCell>
                        <TextField
                          type="number"
                          variant="outlined"
                          data-test="add-time-minutes-field"
                          fullWidth
                          placeholder={"mm"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                &nbsp;minutes
                              </InputAdornment>
                            ),
                          }}
                          defaultValue={0}
                        />
                      </TableCell>
                    }
                  />
                </TableRow>
              </TableBody>
            </Table>
            {(errors.timeDays || errors.timeHours || errors.timeMinutes) && (
              <Error data-test="time-validation">
                Duration error
              </Error>
            )}
          </Grid>
          <br />
          <Grid item xs={12}>
            <Controller
              control={control}
              name={nameof<LcmTimeRecord>((x) => x.forecast)}
              rules={{
                pattern: {
                  value: /^\d{1,15}$|(?=^.{1,15}$)^\d+\.\d{0,2}$/,
                  message:
                    "Entered value does not match the format of currency",
                },
              }}
              as={
                <TextField
                  type="number"
                  variant="outlined"
                  label={fieldLabels.forecast}
                  data-test="add-time-forecast-field"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">£</InputAdornment>
                    ),
                  }}
                />
              }
            />
            {errors.forecast?.type === "pattern" && (
              <Error data-test="add-time-forecast-validation">
                {errors.forecast.message}
              </Error>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-add-time-dialog"
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          data-test="add-time-message-button"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
